import { Link } from 'react-router-dom'
import { muxConfig } from '../../config/mux.config'
import type { VideoWithProvider } from '../../services/videoService'

interface VideoCardProps {
  video: VideoWithProvider
  showProvider?: boolean
}

export function VideoCard({ video, showProvider = true }: VideoCardProps) {
  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Format view count
  const formatViews = (count: number | null) => {
    if (!count) return '0 views'
    if (count === 1) return '1 view'
    if (count < 1000) return `${count} views`
    if (count < 1000000) return `${(count / 1000).toFixed(1)}K views`
    return `${(count / 1000000).toFixed(1)}M views`
  }

  // Get thumbnail URL with custom time support
  const thumbnailTime = video.thumbnail_time
    ?? (video.duration_seconds ? video.duration_seconds * 0.25 : 0)

  const thumbnailUrl = video.mux_playback_id
    ? muxConfig.getThumbnailUrl(video.mux_playback_id, {
        width: 640,
        height: 360,
        time: thumbnailTime,
      })
    : video.thumbnail_url || '/placeholder-video.jpg'

  return (
    <Link
      to={`/videos/${video.id}`}
      className="group block bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gray-100 overflow-hidden">
        <img
          src={thumbnailUrl}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />

        {/* Duration badge */}
        <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/75 text-white text-xs rounded">
          {formatDuration(video.duration_seconds)}
        </span>

        {/* Status badge (for non-ready videos) */}
        {video.status !== 'ready' && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded capitalize">
            {video.status}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {video.title}
        </h3>

        {showProvider && video.user && (
          <p className="mt-1 text-sm text-gray-600">
            {video.user.full_name || 'Unknown Provider'}
          </p>
        )}

        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
          <span>{formatViews(video.view_count)}</span>
          <span>·</span>
          <span>{video.like_count || 0} likes</span>
        </div>

        {/* Tags */}
        {video.tags && video.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {video.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
            {video.tags.length > 3 && (
              <span className="text-xs text-gray-400">
                +{video.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  )
}
